/**
 * Calls Page - Call Session History (Epic 13D)
 *
 * Displays all call sessions for the team with:
 * - Status filter tabs (All/Active/Completed/Archived)
 * - Search by client name
 * - Click to view session details
 *
 * Per design.md v0.4:
 * - Uses semantic tokens
 * - lucide icons only
 * - BlurFade animations
 * - Uses @uprosal/ui components
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../../convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
    Badge, Button, Skeleton, Input,
    Phone, Search, Clock, User, FileText, Trash2, Archive, Check, Eye,
} from '@uprosal/ui';
import { BlurFade } from '../../components/BlurFade';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CallDetailDialog } from './CallDetailDialog';

type StatusFilter = 'all' | 'active' | 'completed' | 'archived';

type CallSessionListItem = {
    _id: Id<'callSessions'>;
    clientId: Id<'clients'>;
    clientName: string;
    status: 'active' | 'completed' | 'archived';
    startedAt?: number;
    endedAt?: number;
    duration?: number;
    sourceUrl: string;
    createdAt: number;
    noteLines: Array<{ id: string; text: string; isActionItem: boolean }>;
    checklist: Array<{ item: string; done: boolean; group: 'during' | 'after' }>;
};

export function CallsPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [selectedSession, setSelectedSession] = useState<Id<'callSessions'> | null>(null);
    const [archiveTarget, setArchiveTarget] = useState<Id<'callSessions'> | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Id<'callSessions'> | null>(null);

    // Fetch sessions
    const sessions = useQuery(api.queries.callSessions.listByTeam, {
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
    }) as CallSessionListItem[] | undefined;

    // Fetch status counts
    const statusCounts = useQuery(api.queries.callSessions.getStatusCounts, {});

    const archiveSession = useMutation(api.mutations.callSessions.archive);
    const deleteSession = useMutation(api.mutations.callSessions.remove);

    const isLoading = sessions === undefined;

    const handleArchive = async () => {
        if (!archiveTarget) return;
        try {
            await archiveSession({ sessionId: archiveTarget });
        } catch (e) {
            console.error('Failed to archive:', e);
        } finally {
            setArchiveTarget(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteSession({ sessionId: deleteTarget });
        } catch (e) {
            console.error('Failed to delete:', e);
        } finally {
            setDeleteTarget(null);
        }
    };

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now.getTime() - timestamp;

        if (diff < 86400000) {
            const hours = Math.floor(diff / 3600000);
            if (hours < 1) return 'Just now';
            return `${hours}h ago`;
        }
        if (diff < 604800000) {
            const days = Math.floor(diff / 86400000);
            return `${days}d ago`;
        }
        return date.toLocaleDateString();
    };

    const formatDuration = (seconds?: number) => {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins >= 60) {
            const hours = Math.floor(mins / 60);
            const remainingMins = mins % 60;
            return `${hours}h ${remainingMins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge variant="success">Active</Badge>;
            case 'completed':
                return <Badge variant="secondary">Completed</Badge>;
            case 'archived':
                return <Badge variant="outline">Archived</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const filteredSessions = (sessions ?? []).filter(session =>
        !searchQuery ||
        session.clientName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const statusFilters: { value: StatusFilter; label: string; count?: number }[] = [
        { value: 'all', label: 'All', count: statusCounts?.total },
        { value: 'active', label: 'Active', count: statusCounts?.active },
        { value: 'completed', label: 'Completed', count: statusCounts?.completed },
        { value: 'archived', label: 'Archived', count: statusCounts?.archived },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <BlurFade delay={0}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Phone className="w-6 h-6" />
                            Call Sessions
                        </h1>
                        <p className="text-muted-foreground">
                            View and manage your client call sessions
                        </p>
                    </div>
                </div>
            </BlurFade>

            {/* Search + Status Filters */}
            <BlurFade delay={0.1}>
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative max-w-md flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search by client name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                        {statusFilters.map((filter) => (
                            <Button
                                key={filter.value}
                                variant={statusFilter === filter.value ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setStatusFilter(filter.value)}
                                className="text-sm"
                            >
                                {filter.label}
                                {filter.count !== undefined && (
                                    <span className="ml-1.5 text-xs opacity-70">
                                        ({filter.count})
                                    </span>
                                )}
                            </Button>
                        ))}
                    </div>
                </div>
            </BlurFade>

            {/* Stats Row */}
            <BlurFade delay={0.15}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Phone className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{statusCounts?.total ?? 0}</p>
                                    <p className="text-sm text-muted-foreground">Total Calls</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-green-500/10">
                                    <Phone className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{statusCounts?.active ?? 0}</p>
                                    <p className="text-sm text-muted-foreground">Active</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10">
                                    <Check className="w-5 h-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{statusCounts?.completed ?? 0}</p>
                                    <p className="text-sm text-muted-foreground">Completed</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-muted">
                                    <Archive className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{statusCounts?.archived ?? 0}</p>
                                    <p className="text-sm text-muted-foreground">Archived</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </BlurFade>

            {/* Sessions List */}
            <BlurFade delay={0.2}>
                <Card>
                    <CardHeader>
                        <CardTitle>Call Sessions</CardTitle>
                        <CardDescription>
                            Your client call sessions with notes and action items
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="space-y-4">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex gap-4 p-4 border border-border rounded-lg">
                                        <Skeleton className="w-full h-20" />
                                    </div>
                                ))}
                            </div>
                        ) : filteredSessions.length === 0 ? (
                            <div className="text-center py-12">
                                <Phone className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                                <p className="text-muted-foreground">
                                    {searchQuery ? 'No sessions match your search' : 'No call sessions yet'}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Use the Call Companion in the Chrome extension during calls
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredSessions.map((session) => {
                                    const actionItems = session.noteLines.filter(n => n.isActionItem).length;
                                    const completedChecklist = session.checklist.filter(c => c.done).length;
                                    const totalChecklist = session.checklist.length;

                                    return (
                                        <div
                                            key={session._id}
                                            className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                            onClick={() => setSelectedSession(session._id)}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    {/* Client name */}
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <User className="w-4 h-4 text-muted-foreground" />
                                                        <h3 className="font-medium">
                                                            {session.clientName}
                                                        </h3>
                                                        {getStatusBadge(session.status)}
                                                    </div>
                                                    {/* Metadata row */}
                                                    <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground mt-2">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDate(session.createdAt)}
                                                        </span>
                                                        {session.duration && (
                                                            <span>
                                                                Duration: {formatDuration(session.duration)}
                                                            </span>
                                                        )}
                                                        {session.noteLines.length > 0 && (
                                                            <span className="flex items-center gap-1">
                                                                <FileText className="w-3 h-3" />
                                                                {session.noteLines.length} notes
                                                                {actionItems > 0 && ` (${actionItems} action items)`}
                                                            </span>
                                                        )}
                                                        {totalChecklist > 0 && (
                                                            <span>
                                                                Checklist: {completedChecklist}/{totalChecklist}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Actions */}
                                                <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setSelectedSession(session._id)}
                                                        title="View details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    {session.status !== 'archived' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setArchiveTarget(session._id)}
                                                            title="Archive session"
                                                        >
                                                            <Archive className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteTarget(session._id)}
                                                        title="Delete session"
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </BlurFade>

            {/* Session Detail Dialog */}
            <CallDetailDialog
                sessionId={selectedSession}
                open={!!selectedSession}
                onOpenChange={(open) => !open && setSelectedSession(null)}
            />

            {/* Archive Confirmation Dialog */}
            <ConfirmDialog
                open={!!archiveTarget}
                onOpenChange={(open) => !open && setArchiveTarget(null)}
                onConfirm={handleArchive}
                title="Archive Session"
                description="Are you sure you want to archive this call session? You can still view it in the Archived tab."
                confirmLabel="Archive"
                cancelLabel="Cancel"
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete Session"
                description="Are you sure you want to delete this call session? This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
            />
        </div>
    );
}
